import {
  HttpStatusCode,
  PejHttpError,
  statusCodeToHttpStatusCode,
} from '@common-lib';

export interface ClientWithBodyProps<T> {
  body: T;
  method: 'PUT' | 'POST' | 'DELETE';
  url: string;
}

export interface BaseClientStorageEngine {
  getToken: () => Promise<string | undefined>;
  setToken: (token: string) => Promise<void>;
}

export const baseClient = {
  body: <T, Result>({ body, method, url }: ClientWithBodyProps<T>) => {
    const stringifiedBody = JSON.stringify(body);

    return executeFetch(url, {
      method,
      body: stringifiedBody,
      headers: { 'content-type': 'application/json' },
    })
      .then(async (res) => {
        await updateSessionIfRefreshed(res);
        await ensureSuccessful(res);

        if (res.status === HttpStatusCode.NO_CONTENT) {
          return undefined;
        }

        return res.json();
      })
      .then((result: Result) => result);
  },
  get: <Result>(url: string, init?: RequestInit) => {
    return executeFetch(url, init)
      .then(async (res) => {
        await updateSessionIfRefreshed(res);
        await ensureSuccessful(res);

        if (res.status === HttpStatusCode.NO_CONTENT) {
          return undefined;
        }

        return res.json();
      })
      .then((result: Result) => result);
  },
  delete: (url: string) => {
    return executeFetch(url, {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
    }).then(async (res) => {
      await updateSessionIfRefreshed(res);
      await ensureSuccessful(res);
    });
  },
  setBaseUrl: (
    url: string,
    extraHeaders: Record<string, string> | undefined,
  ) => {
    baseUrl = url;
    baseHeaders = extraHeaders;
  },
  setStorageEngine: (engine: BaseClientStorageEngine) => {
    storageEngine = engine;
  },
};

const retryableErrors = [
  HttpStatusCode.SERVICE_UNAVAILABLE,
  HttpStatusCode.BAD_GATEWAY,
  HttpStatusCode.GATEWAY_TIMEOUT,
];

let baseUrl = '';
let baseHeaders: Record<string, string> | undefined;
let storageEngine: BaseClientStorageEngine | undefined;

const executeFetch = async (
  url: string,
  options?: RequestInit,
  attempt = 1,
  firstTryTimestamp = Date.now(),
): Promise<Response> => {
  const response = await fetch(`${baseUrl}${url}`, {
    ...options,
    headers: {
      ...baseHeaders,
      ...options?.headers,
      ...(await getAuthorizationHeadersIfAvailable()),
    },
  });

  if (Date.now() - firstTryTimestamp > 30000) {
    /*
     * If an API is down the BFF will do a similar retry logic.
     * This leads to every attempt made by the frontend to wait
     * a long time. To prevent this unnecessary cycle we have a
     * maximum wait timeout here in the frontend. If reached we
     * simply stop retrying.
     */
    return response;
  }

  // 20 attempts is ~35 seconds
  if (attempt < 20 && retryableErrors.includes(response.status)) {
    await new Promise((resolve) =>
      setTimeout(resolve, Math.min(200 * attempt, 2500)),
    );

    return await executeFetch(url, options, attempt + 1, firstTryTimestamp);
  }

  return response;
};

const getAuthorizationHeadersIfAvailable = async () => {
  if (!storageEngine) {
    throw Error(
      'Storage engine is not set. Please call baseClient.setStorageEngine() before using the client.',
    );
  }

  const sessionToken = await storageEngine.getToken();

  if (sessionToken) {
    return {
      Authorization: `Bearer ${sessionToken}`,
    };
  }

  return undefined;
};

const updateSessionIfRefreshed = async (res: Response) => {
  const refreshedSession =
    res.headers.get('X-Refreshed-Session') ||
    res.headers.get('x-refreshed-session');

  if (refreshedSession) {
    if (!storageEngine) {
      throw Error(
        'Storage engine is not set. Please call baseClient.setStorageEngine() before using the client.',
      );
    }

    await storageEngine.setToken(refreshedSession);
  }
};

const ensureSuccessful = async (res: Response) => {
  if (!res.ok) {
    const errorMessage = await res.text();

    const statusCode = statusCodeToHttpStatusCode[res.status];

    if (!statusCode) {
      throw Error(
        `Unknown error occurred, status code: ${statusCode ?? res.status}`,
      );
    }

    if (errorMessage.startsWith('{')) {
      const errorObject = JSON.parse(errorMessage);

      throw new PejHttpError(
        statusCode,
        errorObject.message,
        errorObject.details,
      );
    }

    throw new PejHttpError(statusCode, errorMessage);
  }
};
